import React, { useState, useEffect, useRef } from "react";

const RangeSlider = ({
  rangeItems,
  handleSelectedPlan,
  selectedValue,
  leadItems,
  unit,
}) => {
  const defaultIndex = rangeItems.indexOf(selectedValue && selectedValue);
  const [selectedItemIndex, setSelectedItemIndex] = useState(
    defaultIndex !== -1 ? defaultIndex : 0
  );
  const [isDragging, setIsDragging] = useState(false);
  const progressBarRef = useRef(null);
  const hideOnMobile = "hidden-on-mobile";

  useEffect(() => {
    setSelectedItemIndex(defaultIndex !== -1 ? defaultIndex : 0);
  }, [defaultIndex]);

  const isMobile =
    window.matchMedia &&
    window.matchMedia("only screen and (max-width: 767px)").matches;

  const handleProgressBar = (index) => {
    handleSelectedPlan && handleSelectedPlan(rangeItems[index]);
    setSelectedItemIndex(index);
  };

  const calculateDotPosition = (index) => {
    const totalItems = rangeItems.length - 1;
    const progressBarWidth = 100 / totalItems;
    return progressBarWidth * index;
  };

  const calculateProgressBarWidth = () => {
    const totalItems = rangeItems.length;
    return (100 / (totalItems - 1)) * selectedItemIndex;
  };

  const renderDots = () => {
    let activeIndexes = [];
    for (let i = 0; i <= selectedItemIndex; i++) {
      activeIndexes.push(i);
    }
    return (
      rangeItems &&
      rangeItems.map((item, index) => (
        <div className="relative" key={index}>
          <span
            onClick={() => handleProgressBar(index)}
            className={`dot absolute bg-[#F1F4F6] rounded-[50%] w-[16px] h-[16px] top-[-4px] cursor-pointer  
          ${activeIndexes.includes(index) ? "active" : ""}`}
            style={{
              left: `${calculateDotPosition(index)}%`,
            }}
          ></span>
          {index === selectedItemIndex && (
            <div
              className="text-on-dot absolute text-center -top-[60px] w-[160px] text-sm -translate-x-2.5 md:-translate-x-2/4 hidden duration-300"
              style={{
                left:
                  index === rangeItems.length - 1
                    ? isMobile
                      ? "50%"
                      : "89%"
                    : index === 0
                    ? isMobile
                      ? "0%"
                      : "10%"
                    : isMobile && index === rangeItems.length - 3
                    ? `calc(${calculateDotPosition(index)}% - 10%)`
                    : isMobile && index >= item.length - 2
                    ? "auto"
                    : `${calculateDotPosition(index)}%`,
                right: isMobile && index >= item.length - 3 ? "0%" : "auto",
              }}
            >
              <strong className="text-base font-black leading-[160%] text-primary hidden md:inline-block">
                {item}
              </strong>
              <br />
              <p className="text-sm leading-[120%] text-[#5C6772] font-bold">
                Leads
              </p>
            </div>
          )}
        </div>
      ))
    );
  };

  const renderLeadDots = () => {
    let activeIndexes = [];
    for (let i = 0; i <= selectedItemIndex; i++) {
      activeIndexes.push(i);
    }
    return (
      leadItems &&
      leadItems.map((item, index) => (
        <div className="relative" key={index}>
          <span
            onClick={() => handleProgressBar(index)}
            className={`dot absolute bg-[#F1F4F6] rounded-[50%] w-[16px] h-[16px] top-[-4px] cursor-pointer  
          ${activeIndexes.includes(index) ? "active" : ""}`}
            style={{
              left: `${calculateDotPosition(index)}%`,
            }}
          ></span>
          <div
            className="text-on-dot absolute text-center -top-[60px] w-[160px] text-sm -translate-x-2.5 md:-translate-x-2/4 hidden duration-300"
            style={{
              left:
                index === leadItems.length - 1
                  ? isMobile
                    ? "50%"
                    : "98%"
                  : `${calculateDotPosition(index)}%`,
              right: isMobile && index >= item.length - 3 ? "0%" : "auto",
            }}
          >
            <strong className="text-base font-black leading-[160%] text-primary hidden md:inline-block">
              {item}
            </strong>
            <br />
            {!isMobile && (
              <p className="text-sm leading-[120%] text-[#5C6772] font-bold">
                Leads
              </p>
            )}
          </div>
        </div>
      ))
    );
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const progressBarRect = progressBarRef.current.getBoundingClientRect();
        const progressBarWidth = progressBarRect.width;
        const clientX =
          e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
        const offsetX = clientX - progressBarRect.left;
        const newPosition = Math.max(
          0,
          Math.min(Math.round((offsetX / progressBarWidth) * 100), 100)
        );

        const newIndex = Math.round(
          newPosition / (100 / (rangeItems.length - 1))
        );
        handleSelectedPlan && handleSelectedPlan(rangeItems[newIndex]);
        setSelectedItemIndex(newIndex);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchStart = (e) => {
      setIsDragging(true);
      handleMouseDown(e.touches[0]);
    };

    const handleTouchMove = (e) => {
      if (isDragging) {
        handleMouseMove(e.touches[0]);
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    const handleMouseDown = (e) => {
      if (e.target === progressBarRef.current) {
        setIsDragging(true);
        handleMouseMove(e);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", handleMouseDown);
    progressBarRef.current.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, rangeItems.length]);

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  return (
    <div className="">
      <div className="">
        <div className="w-full mx-auto px-5">
          <h4 className="sr-only">Status</h4>

          <div
            ref={progressBarRef}
            className="relative h-2 bg-gray-200 rounded-full"
            onMouseDown={handleMouseDown}
            // onTouchStart={handleMouseDown}
          >
            <div
              className="absolute top-0 left-0 h-full bg-primary rounded-full duration-500"
              style={{ width: `${calculateProgressBarWidth()}%` }}
            ></div>

            {leadItems ? renderLeadDots() : renderDots()}
          </div>
          {!isMobile ? (
            <ul className="range-items mt-4 flex justify-between text-sm font-medium text-gray-600" style={{margin: '35px -20px 0'}}>
              {rangeItems.map((item, index) => (
                <li
                  key={index}
                  onClick={() => handleProgressBar(index)}
                  className={`text-[16px] font-medium leading-[160%] text-[#869099] cursor-pointer ${
                    index === selectedItemIndex ? "active" : ""
                  }`}
                >
                  {item}
                  {unit && unit}
                </li>
              ))}
            </ul>
          ) : (
            <div
              className={`range-items inline-block text-[16px] relative mt-4 font-bold leading-[160%] text-primary cursor-pointer -translate-x-5 ${!hideOnMobile}`}
              style={{
                left: `${
                  selectedItemIndex !== null
                    ? selectedItemIndex === rangeItems.length - 1
                      ? "93%"
                      : `${calculateDotPosition(selectedItemIndex)}%`
                    : ""
                }`,
              }}
            >
              {selectedItemIndex !== null && unit
                ? rangeItems[selectedItemIndex] + unit
                : selectedItemIndex !== null
                ? rangeItems[selectedItemIndex]
                : ""}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RangeSlider;
